import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, Wallet, Percent, PiggyBank, CircleDollarSign,
  ArrowUpRight, ArrowDownRight, Info, AlertTriangle, CheckCircle2, Lightbulb, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatINR, formatDate, CHART_COLORS } from "@/lib/format";
import { getRange, rangeParams } from "@/lib/period";
import PeriodFilter from "@/components/PeriodFilter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const KPI = ({ label, value, icon: Icon, color, sub, testid }) => (
  <div className="sb-fade-in rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
    <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-full ${color}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="text-sm font-medium text-slate-500">{label}</div>
    <div className="font-heading text-2xl font-bold tracking-tight text-slate-900">{value}</div>
    {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
  </div>
);

const ChartCard = ({ title, right, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
    <div className="mb-4 flex items-center justify-between">
      <h3 className="font-heading font-bold text-slate-900">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const Donut = ({ data }) => {
  if (!data || data.length === 0)
    return <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">No data for this period</div>;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="32%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2}>
          {data.map((_, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
        </Pie>
        <Tooltip formatter={(v) => formatINR(v)} />
        <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" wrapperStyle={{ fontSize: 12, lineHeight: "1.6" }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

function LossCoach({ data, trend }) {
  const exp = [...(data.expense_breakdown || [])].sort((a, b) => b.value - a.value);
  const top = exp[0];
  const topShare = data.total_expenses > 0 && top ? (top.value / data.total_expenses) * 100 : 0;

  let mom = null;
  if (trend.length >= 2) {
    const prev = trend[trend.length - 2];
    const cur = trend[trend.length - 1];
    if (prev.expenses > 0) {
      const chg = ((cur.expenses - prev.expenses) / prev.expenses) * 100;
      if (chg >= 3) mom = { up: true, text: `Spending rose ${chg.toFixed(0)}% vs ${prev.month}.` };
      else if (chg <= -3) mom = { up: false, text: `Spending fell ${Math.abs(chg).toFixed(0)}% vs ${prev.month}.` };
    }
  }

  let tone, Icon, title, body;
  if (data.is_loss) {
    tone = "rose"; Icon = AlertTriangle;
    title = "Spending outpaced revenue this period";
    body = `You spent ${formatINR(data.total_expenses)} against ${formatINR(data.total_revenue)} in revenue — a net loss of ${formatINR(Math.abs(data.net_profit))}.`;
  } else if (data.total_revenue === 0 && data.total_expenses > 0) {
    tone = "rose"; Icon = AlertTriangle;
    title = "Expenses recorded, but no revenue yet";
    body = `You've spent ${formatINR(data.total_expenses)} with no revenue logged for this period.`;
  } else if (data.profit_margin !== null && data.profit_margin < 15 && data.total_revenue > 0) {
    tone = "amber"; Icon = Lightbulb;
    title = "Your profit is running thin";
    body = `Margin is just ${data.profit_margin.toFixed(1)}%. Trimming a large cost could noticeably lift profit.`;
  } else {
    tone = "emerald"; Icon = ShieldCheck;
    title = "Your finances look healthy";
    body = `Revenue of ${formatINR(data.total_revenue)} comfortably covers ${formatINR(data.total_expenses)} in costs${data.profit_margin !== null ? ` at a ${data.profit_margin.toFixed(1)}% margin` : ""}.`;
  }

  const suggestion = top && data.total_expenses > 0
    ? `Biggest cost to review: ${top.name} — ${formatINR(top.value)} (${topShare.toFixed(0)}% of all expenses).`
    : "Add a few expenses to unlock tailored cost insights.";

  const toneMap = {
    rose: { card: "border-rose-200 bg-rose-50", icon: "bg-rose-500", title: "text-rose-800", body: "text-rose-700", pill: "bg-white text-rose-700 border-rose-200" },
    amber: { card: "border-amber-200 bg-amber-50", icon: "bg-amber-500", title: "text-amber-800", body: "text-amber-700", pill: "bg-white text-amber-700 border-amber-200" },
    emerald: { card: "border-emerald-200 bg-emerald-50", icon: "bg-emerald-500", title: "text-emerald-800", body: "text-emerald-700", pill: "bg-white text-emerald-700 border-emerald-200" },
  };
  const t = toneMap[tone];

  return (
    <div className={`sb-fade-in flex flex-col gap-4 rounded-xl border p-5 shadow-sm sm:flex-row sm:items-start ${t.card}`} data-testid="loss-coach">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white ${t.icon}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className={`font-heading font-bold ${t.title}`}>Monthly Insight</h3>
          {mom && (
            <span className={`inline-flex items-center gap-1 text-xs font-medium ${mom.up ? "text-rose-600" : "text-emerald-600"}`}>
              {mom.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{mom.text}
            </span>
          )}
        </div>
        <div className={`mt-0.5 text-sm font-semibold ${t.title}`}>{title}</div>
        <p className={`mt-1 text-sm ${t.body}`}>{body}</p>
        <div className={`mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium ${t.pill}`} data-testid="loss-coach-suggestion">
          <Lightbulb className="h-4 w-4" />{suggestion}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { version, openTransaction } = useApp();
  const [periodKey, setPeriodKey] = useState("this_month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [range, setRange] = useState(getRange("this_month"));
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [trendMonths, setTrendMonths] = useState("6");
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/dashboard", { params: rangeParams(range) }).then((r) => setData(r.data));
    api.get("/transactions/recent", { params: { limit: 10 } }).then((r) => setRecent(r.data));
  }, [range, version]);

  useEffect(() => {
    api.get("/dashboard/trend", { params: { months: Number(trendMonths) } }).then((r) => setTrend(r.data));
  }, [trendMonths, version]);

  if (!data) return <div className="p-8 text-slate-400">Loading dashboard…</div>;

  const margin = data.profit_margin === null ? "N/A" : `${data.profit_margin.toFixed(2)}%`;
  const profitPct = data.total_revenue > 0 ? Math.round((data.allocated_profit + data.unallocated_profit) > 0 ? (data.allocated_profit / (data.net_profit || 1)) * 100 : 0) : 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">SocietyBites Financial Dashboard</h1>
          <p className="mt-1 text-slate-500">Overview of your business finances</p>
        </div>
        <PeriodFilter periodKey={periodKey} setPeriodKey={setPeriodKey} custom={custom} setCustom={setCustom} onChange={setRange} />
      </div>

      {/* Alerts */}
      <div className="grid gap-3 md:grid-cols-3">
        {data.is_loss ? (
          <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="alert-loss">
            <AlertTriangle className="h-5 w-5 text-rose-500" />
            <div className="text-sm text-rose-700">SocietyBites recorded a <b>net loss of {formatINR(Math.abs(data.net_profit))}</b> for this period.</div>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <div className="text-sm text-emerald-700"><b>All good!</b> Your finances are looking healthy.</div>
          </div>
        )}
        <Link to="/allocation" className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3 transition-colors hover:bg-blue-100" data-testid="alert-unallocated">
          <Info className="h-5 w-5 text-blue-500" />
          <div className="text-sm text-blue-700">{formatINR(data.unallocated_profit)} profit remains unallocated.</div>
        </Link>
        <Link to="/reports" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 transition-colors hover:bg-slate-50">
          <PiggyBank className="h-5 w-5 text-orange-500" />
          <div className="text-sm text-slate-600">View detailed financial reports.</div>
        </Link>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KPI testid="kpi-revenue" label="Total Revenue" value={formatINR(data.total_revenue)} icon={TrendingUp} color="bg-emerald-100 text-emerald-600" />
        <KPI testid="kpi-expenses" label="Total Expenses" value={formatINR(data.total_expenses)} icon={TrendingDown} color="bg-rose-100 text-rose-600" />
        <KPI testid="kpi-profit" label={data.is_loss ? "Net Loss" : "Net Profit"} value={formatINR(Math.abs(data.net_profit))} icon={Wallet} color="bg-blue-100 text-blue-600" />
        <KPI testid="kpi-margin" label="Profit Margin" value={margin} icon={Percent} color="bg-purple-100 text-purple-600" />
        <KPI testid="kpi-allocated" label="Allocated Profit" value={formatINR(data.allocated_profit)} icon={PiggyBank} color="bg-orange-100 text-orange-600" sub={data.net_profit > 0 ? `${profitPct}% of Profit` : ""} />
        <KPI testid="kpi-unallocated" label="Unallocated Profit" value={formatINR(data.unallocated_profit)} icon={CircleDollarSign} color="bg-teal-100 text-teal-600" />
      </div>

      {/* Monthly insight / loss coaching */}
      <LossCoach data={data} trend={trend} />

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard testid="chart-rev-vs-exp" title="Revenue vs Expenses" right={
          <Select value={trendMonths} onValueChange={setTrendMonths}>
            <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="6">Last 6 Months</SelectItem>
              <SelectItem value="12">Last 12 Months</SelectItem>
            </SelectContent>
          </Select>
        }>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v / 1000}k`} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              <Bar dataKey="revenue" name="Revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testid="chart-expense-breakdown" title="Expense Breakdown"><Donut data={data.expense_breakdown} /></ChartCard>
        <ChartCard testid="chart-revenue-breakdown" title="Revenue Breakdown"><Donut data={data.revenue_breakdown} /></ChartCard>
        <ChartCard testid="chart-allocation" title="Profit Allocation">
          <Donut data={data.allocations.map((a) => ({ name: a.name, value: a.amount }))} />
        </ChartCard>
      </div>

      {/* Recent + Allocation summary */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm lg:col-span-2" data-testid="recent-transactions">
          <div className="flex items-center justify-between border-b border-slate-100 p-5">
            <h3 className="font-heading font-bold text-slate-900">Recent Transactions</h3>
            <Link to="/revenue" className="text-sm font-medium text-orange-600 hover:underline">View All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-3">Date</th><th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Category</th><th className="px-5 py-3">Description</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No transactions yet</td></tr>}
                {recent.map((t) => (
                  <tr key={`${t.type}-${t.id}`} className="cursor-pointer border-t border-slate-50 transition-colors hover:bg-slate-50"
                      onClick={() => openTransaction(t.type, null)}>
                    <td className="px-5 py-3 text-slate-600">{formatDate(t.date)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${t.type === "revenue" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {t.type === "revenue" ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        {t.type === "revenue" ? "Revenue" : "Expense"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{t.category}</td>
                    <td className="px-5 py-3 text-slate-500">{t.description}</td>
                    <td className={`px-5 py-3 text-right font-semibold ${t.type === "revenue" ? "text-emerald-600" : "text-rose-600"}`}>
                      {t.type === "revenue" ? "" : "-"}{formatINR(t.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="allocation-summary">
          <h3 className="mb-4 font-heading font-bold text-slate-900">Profit Allocation Summary</h3>
          <div className="space-y-3">
            {data.allocations.map((a, i) => (
              <div key={a.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-sm text-slate-600">{a.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-slate-900">{formatINR(a.amount)}</div>
                  <div className="text-xs text-slate-400">{a.percentage}%</div>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="font-semibold text-slate-900">Total Allocated</span>
              <span className="font-heading font-bold text-orange-600">{formatINR(data.allocated_profit)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
